import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import tokens from '@lacedltd/design-tokens'
import { Typography, mediaQuery } from '@lacedltd/laced-components'
import type { CSSObject } from '@emotion/styled'

const pulseAnimation = keyframes`
  0% {
    background-color: ${tokens.color.airJordan[100]}
  }
  50% {
    background-color: ${tokens.color.airJordan[200]}
  }
  100% {
    background-color: ${tokens.color.airJordan[100]}
  }
`

export const SellerVerificationForm = styled.form({
  width: '100%'
})

export const StepContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%'
})

export const SellerTypes = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[6],
  width: '100%',
  padding: `0 ${tokens.spacing[6]}`,

  [mediaQuery.m.min]: {
    padding: '0'
  }
})

export const Step = styled.fieldset({
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[6],
  width: '100%',
  padding: `${tokens.spacing[7]} ${tokens.spacing[6]}`,
  background: tokens.color.yeezy[0],

  h3: {
    margin: 0
  },

  p: {
    margin: 0,
    fontSize: tokens.fontSize[1]
  },

  [mediaQuery.m.min]: {
    padding: tokens.spacing[8]
  }
})

export const SellerTypeHeading = styled(Typography.Heading)({
  fontSize: tokens.fontSize[3],
  marginBottom: tokens.spacing[2]
})

export const SellerTypeSubHeading = styled(Typography.Text)({
  fontSize: tokens.fontSize[3],
  lineHeight: tokens.spacing[6],
  color: tokens.color.airJordan[500]
})

export const SellerTypeInfo = styled(Typography.Text)({
  display: 'block',
  marginTop: tokens.spacing[5],
  color: tokens.color.airJordan[400]
})

export const SellerType = styled.label<{ disabled?: boolean }>(
  ({ disabled }) => {
    const disabledStyles: CSSObject = {
      backgroundColor: tokens.color.airJordan[100],
      pointerEvents: 'none',

      [`& ${SellerTypeHeading}, & ${SellerTypeSubHeading}, & ${SellerTypeInfo}`]:
        {
          color: tokens.color.airJordan[400]
        }
    }

    return {
      background: tokens.color.yeezy[0],
      padding: tokens.spacing[6],
      border: `${tokens.borderWidth.xs} solid ${tokens.color.airJordan[100]}`,
      position: 'relative',
      cursor: 'pointer',
      transition: 'border 500ms ease-out',
      paddingRight: tokens.spacing[10],

      '&:hover': {
        borderColor: tokens.color.airJordan[400]
      },

      img: {
        position: 'absolute',
        right: tokens.spacing[6],
        top: '50%',
        transform: 'translateY(-50%) rotate(90deg)',
        opacity: disabled ? 0.5 : 1
      },

      ...(disabled && disabledStyles)
    }
  }
)

export const InputContainer = styled.div<{ inline?: boolean; dob?: boolean }>(
  ({ inline, dob }) => ({
    display: 'flex',
    gap: inline
      ? `${tokens.spacing[2]} ${tokens.spacing[3]}`
      : tokens.spacing[5],
    flexWrap: 'wrap',
    flexDirection: inline ? 'row' : 'column',

    '> div:nth-of-type(2)': {
      minWidth: dob ? '37.5%' : 'auto'
    },

    '> p:last-of-type': {
      marginTop: !inline ? `-${tokens.spacing[4]}` : 'initial'
    },

    [mediaQuery.m.min]: {
      flexDirection: 'row',
      gap: `${tokens.spacing[2]} ${tokens.spacing[5]}`,

      '> p:last-of-type': {
        marginTop: 0
      }
    }
  })
)

export const Input = styled.div<{ hasError?: boolean }>(({ hasError }) => ({
  flexGrow: 1,
  flexBasis: 0,

  'input, select': {
    borderColor: hasError ? tokens.color.roshe[600] : 'auto',

    '&:focus': {
      borderColor: hasError ? tokens.color.roshe[600] : 'auto'
    }
  },

  p: {
    margin: `${tokens.spacing[2]} 0 0`,
    fontSize: tokens.fontSize[1],
    color: tokens.color.roshe[600]
  },

  'div a': {
    fontSize: tokens.fontSize[1],
    textDecoration: 'underline'
  },

  // react-phone-input-2 custom style
  '.react-tel-input': {
    input: {
      width: '100%',
      height: '3.5rem',
      borderColor: hasError
        ? tokens.color.roshe[600]
        : tokens.color.airJordan[400],

      '&:focus': {
        boxShadow: 'none',
        borderColor: hasError
          ? tokens.color.roshe[600]
          : tokens.color.airJordan[400]
      }
    },

    '.flag-dropdown': {
      width: '4.375rem',

      '.selected-flag': {
        width: '100%',
        paddingLeft: tokens.spacing[5],

        '&:before': {
          display: 'none'
        }
      }
    }
  }
}))

export const RadioInputContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',

  '> div': {
    display: 'flex',
    flexDirection: 'column'
  }
})

export const RadioInput = styled.label<{ checked?: boolean }>(
  ({ checked }) => ({
    display: 'flex',
    height: tokens.spacing[7],
    alignItems: 'center',
    fontSize: tokens.fontSize[1],
    color: tokens.color.airJordan.p,
    position: 'relative',
    paddingLeft: tokens.spacing[7],
    cursor: 'pointer',

    '&:before': {
      content: '""',
      position: 'absolute',
      transform: 'translateY(-50%)',
      width: '1.25rem',
      height: '1.25rem',
      borderRadius: tokens.spacing[6],
      border: `${tokens.borderWidth.xs} solid ${tokens.color.airJordan.p}`,
      top: '50%',
      left: 0,
      backgroundColor: checked ? tokens.color.airJordan.p : 'transparent'
    },

    '&:after': {
      content: '""',
      position: 'absolute',
      transform: 'translateY(-50%)',
      width: '0.625rem',
      height: '0.625rem',
      backgroundColor: tokens.color.yeezy[0],
      top: '50%',
      left: '0.3125rem',
      borderRadius: tokens.spacing[6]
    }
  })
)

export const InputLoading = styled.span({
  display: 'flex',
  flexGrow: 1,
  flexBasis: 0,
  height: '3.5rem',
  background: tokens.color.airJordan[100],
  borderRadius: tokens.borderRadius.m,
  animation: `${pulseAnimation} 1s infinite`
})

export const FormLabel = styled.label<{
  error?: boolean
  noMargin?: boolean
  checkbox?: boolean
  checked?: boolean
}>(({ error, noMargin, checkbox, checked }) => {
  const checkboxStyles: CSSObject = {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    cursor: 'pointer',

    span: {
      display: 'flex',
      marginRight: tokens.spacing[4],
      minWidth: tokens.spacing[5],
      minHeight: tokens.spacing[5],
      border: `${tokens.borderWidth.xs} solid ${tokens.color.airJordan[600]}`,
      borderRadius: tokens.borderRadius.s,
      background: checked ? tokens.color.airJordan.p : 'transparent',
      alignItems: 'center',
      justifyContent: 'center',

      img: {
        width: '75%'
      }
    },

    '&:hover': {
      '&:before': {
        borderColor: tokens.color.airJordan.p
      }
    }
  }

  return {
    fontSize: tokens.fontSize[1],
    color: error ? tokens.color.roshe[600] : 'initial',
    minWidth: '100%',
    // Global styles on label and I dont want to add a class to every label I use so using important
    margin: `0 0 ${noMargin ? 0 : tokens.spacing[2]} !important`,

    ...(checkbox && checkboxStyles)
  }
})

export const FormHint = styled.p<{ error?: boolean }>(({ error }) => ({
  color: error ? tokens.color.roshe[600] : tokens.color.foamRunner[500],
  fontSize: tokens.fontSize[1],
  minWidth: '100%',
  margin: 0
}))
